import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Price from '@nykaa/product-card-desktop/components/CardInfo/Price';
import ToolTip from '@nykaa/delivery-desktop/components/ToolTip';
import Exclamation from '@nykaa/ui-components/Icons/alert-info';
import { getEffectiveSecondaryTag } from '@nykaa/product-card-shared/utils';
import { pushData } from '@nykaa/data-layer';
import styled from 'App/styled';
import { State } from 'shared/store/types';
import { ELEMENT_SIZE } from 'shared/constants';
import { selectCurrentSku } from 'shared/store/product/selector';
import { isConfigurable } from 'helpers/utils/product';
import { INCLUSIVE_TAX_TEXT } from 'shared/constants/productPage';
import Rating from './Rating';
import ReturnDetails from './ReturnDetails';
import Variants from '../Variants';
import Delivery from '../DeliverySection';
import Offers from '../Offers';
import SiteWidePricesRevealed from '../PriceReveal';
import Coupons from '../Coupons';
import { useCouponEligibility } from 'hooks/Product/coupons';
import InlinePriceReveal from '../InlinePriceReveal';
import { useIsTagsV2Enabled } from 'hooks/Product/tags';

interface ProductInfoProps {
  handleShowCAB: () => void;
}

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing.spacing60};
  padding: ${({ theme }) => theme.spacing.spacing60};
  margin-bottom: ${({ theme }) => theme.spacing.spacing60};
  background: ${({ theme }) => theme.colors.white};
`;
const TaxInfo = styled.div`
  ${({ theme }) => theme.typography.bodyLarge};
  padding-bottom: ${({ theme }) => theme.spacing.spacing60};
  ${({ theme }) => theme.spacing.spacing40} 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: contents;
`;

const Title = styled.h1`
  ${({ theme }) => theme.typography.type201};
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding-bottom: ${({ theme }) => theme.spacing.spacing20};
  @media (min-width: 768px) and (max-width: 1024px) {
    ${({ theme }) => theme.typography.type142};
    line-height: unset;
  }
`;
const Size = styled.span`
  ${({ theme }) => theme.typography.subTitleLarge};
  display: block;
  margin: 0;
  margin-top: ${({ theme }) => theme.spacing.spacing20};
  @media (min-width: 768px) and (max-width: 1024px) {
    ${({ theme }) => theme.typography.type142};
    line-height: unset;
  }
`;

const Details = styled.div`
  margin-left: ${({ theme }) => theme.spacing.spacing60};
`;

const PriceCont = styled.div`
  margin-bottom: -${({ theme }) => theme.spacing.spacing60};
  div {
    display: inline-flex !important;
  }
`;

const TaxInfoWrap = styled.div<{ contentClass: string }>`
  display: block;
  padding-bottom: ${({ theme }) => theme.spacing.spacing80};
`;

const TaxInfoIcon = styled.div<{ contentClass: string }>`
  position: absolute;
  cursor: pointer;
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing.spacing20};
  margin-top: ${({ theme }) => theme.spacing.spacing10};
  &:hover {
    .${({ contentClass }) => contentClass} {
      visibility: visible;
      left: -70px;
      bottom: 30px;
    }
  }
  svg {
    path {
      fill: ${({ theme }) => theme.colors.textSecondary};
    }
  }
`;

const SecondaryTag = styled.img`
  height: 18px;
`;

const ProductInfo = ({ handleShowCAB }: ProductInfoProps) => {
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const isMultiMrpEnabled = useSelector(
    (state: State) => state.remoteConfig.switches?.isMultiMrpEnabled || false
  );
  const isMultiCouponsEnabled = useCouponEligibility();
  const secondaryTag = currentSku?.secondaryTag || '';
  const secondaryTagV2 = currentSku?.secondaryTagV2 || '';
  const secondaryTagEnabled = useSelector(
    (state: State) => state.remoteConfig.switches?.secondaryTagEnabledPDP
  );
  const secondaryTagV2Enabled = useIsTagsV2Enabled();
  const {
    name,
    packSize,
    offerPrice,
    discount,
    mrp,
    productType = '',
    multiMrp,
  } = currentSku || {};

  const effectiveTag = getEffectiveSecondaryTag({
    secondaryTag,
    secondaryTagV2,
    tagsOnListingEnabled: secondaryTagEnabled,
    tagsV2Enabled: secondaryTagV2Enabled,
  });
  useEffect(() => {
    const tagV2Eligible = !!secondaryTagV2;
    const tagV2Visible = tagV2Eligible && secondaryTagV2Enabled;
    pushData({
      isTagV2Eligible: tagV2Eligible,
      isTagV2Visible: tagV2Visible,
    });
    return () => {
      pushData({
        isTagV2Eligible: false,
        isTagV2Visible: false,
      });
    };
  }, [secondaryTagV2, secondaryTagV2Enabled]);
  return (
    <Container>
      <Details>
        <Title>
          {name}
          {packSize && !isConfigurable(productType) && (
            <Size>({packSize})</Size>
          )}
        </Title>
        {effectiveTag && <SecondaryTag src={effectiveTag} />}
        <Rating />
        <PriceCont>
          <Price
            mrp={mrp}
            price={offerPrice}
            discount={discount}
            size={ELEMENT_SIZE.MEDIUM}
          />
        </PriceCont>
        <TaxInfoWrap contentClass="taxInfo">
          <TaxInfo>{INCLUSIVE_TAX_TEXT}</TaxInfo>
          {isMultiMrpEnabled &&
            multiMrp &&
            multiMrp?.enabled &&
            multiMrp.description && (
              <TaxInfoIcon contentClass="taxInfo">
                <Exclamation size="20px" />
                <ToolTip
                  message={multiMrp.description || ''}
                  contentClass="taxInfo"
                />
              </TaxInfoIcon>
            )}
        </TaxInfoWrap>
        <InlinePriceReveal />
        {isMultiCouponsEnabled && <Coupons />}
        <Offers />
        <SiteWidePricesRevealed />
        <Variants />
        <Delivery handleShowCAB={handleShowCAB} />
      </Details>
      <ReturnDetails />
    </Container>
  );
};

export default ProductInfo;
